import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import { Paper, Popper } from '@mui/material'

import Direction from '@/enums/direction.enum'
import ClickAwayListener from '@mui/material/ClickAwayListener'

const useStyles = makeStyles(theme => ({
  popperContent: {
    padding: 0,
  },
  popper: {
    zIndex: 1501, // to be over the Tooltip component
    '&[data-popper-placement*="bottom"] $arrow': {
      top: 0,
      left: 0,
      marginTop: '-0.71em',
      marginLeft: 4,
      marginRight: 4,
      '&::before': {
        transformOrigin: '0 100%',
      },
    },
    '&[data-popper-placement*="top"] $arrow': {
      bottom: 0,
      left: 0,
      marginBottom: '-0.71em',
      marginLeft: 4,
      marginRight: 4,
      '&::before': {
        transformOrigin: '100% 0',
      },
    },
    '&[data-popper-placement*="right"] $arrow': {
      left: 0,
      marginLeft: '-0.71em',
      height: '1em',
      width: '0.71em',
      marginTop: 4,
      marginBottom: 4,
      '&::before': {
        transformOrigin: '100% 100%',
      },
    },
    '&[data-popper-placement*="left"] $arrow': {
      right: 0,
      marginRight: '-0.71em',
      height: '1em',
      width: '0.71em',
      marginTop: 4,
      marginBottom: 4,
      '&::before': {
        transformOrigin: '0 0',
      },
    },
  },
  arrow: {
    overflow: 'hidden',
    position: 'absolute',
    width: '1em',
    height: '0.71em' /* = width / sqrt(2) = (length of the hypotenuse) */,
    boxSizing: 'border-box',
    color: 'white',
    '&::before': {
      boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
      content: '""',
      margin: 'auto',
      display: 'block',
      width: '100%',
      height: '100%',
      backgroundColor: 'white',
      transform: 'rotate(45deg)',
    },
  },
}))

export default function PopperMenu(props) {
  const classes = useStyles(props)

  const {
    open = false,
    onClose,
    placement = Direction.BOTTOM,
    arrow = true,
    elementRef,
    children,
    style = {},
    onClick,
    disablePortal = false,
    popperClass,
    scrollElId = null,
    modifiers = [],
  } = props
  const [arrowRef, setArrowRef] = useState(null)
  const popperRef = useRef()

  // This workaround is necessary because popper can't re-evaluate it's position if we are using react-perfect-scrollbar
  useEffect(() => {
    if (!scrollElId) { return }

    const onScrollBar = () => {
      if (!popperRef?.current || !open) { return }
      popperRef?.current?.update()
    }

    document.getElementById(scrollElId).addEventListener('scroll', onScrollBar)
    return () => {
      document.getElementById(scrollElId)?.removeEventListener('scroll', onScrollBar)
    }
  }, [scrollElId, open])

  return (
    <Popper
      onClick={onClick}
      placement={placement}
      open={open}
      popperRef={popperRef}
      disablePortal={disablePortal}
      anchorEl={elementRef?.current || elementRef}
      className={clsx(classes.popper, popperClass && popperClass)}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 5],
          },
        },
        {
          name: 'arrow',
          enabled: arrow,
          options: {
            element: arrowRef,
          },
        },
        ...modifiers,
      ]}
    >
      {arrow && <div className={classes.arrow} ref={setArrowRef} />}
      <ClickAwayListener onClickAway={(event) => onClose && onClose(event)}>
        <Paper className={clsx(classes.paper, style.paper)}>
          <div className={clsx(classes.popperContent, style.popperContent)}>
            {children}
          </div>
        </Paper>
      </ClickAwayListener>
    </Popper>
  )
}

PopperMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  elementRef: PropTypes.any,
  arrow: PropTypes.bool,
  disablePortal: PropTypes.bool,
  onClose: PropTypes.func,
  placement: PropTypes.oneOf([
    Direction.BOTTOM,
    Direction.TOP,
    Direction.LEFT,
    Direction.RIGHT,
    Direction.BOTTOM_START,
    Direction.BOTTOM_END,
  ]),
  popperClass: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  modifiers: PropTypes.array,
  scrollElId: PropTypes.string,
  style: PropTypes.object,
}
